/**
 * Extract warnings from batch job result JSON
 * Handles two formats:
 * 1. Syndication multi-strategy: json_result.strategies (with warnings at top level)
 * 2. Broadcast: json_result.data (with warnings at top level)
 * @param jobResultJson - The job result JSON string or parsed object
 * @returns Array of warning objects
 */
export interface PlanWarning {
  type: string;
  message: string;
  scheduleName?: string;
  source?: 'autopodder' | 'constraint-check';
  slotId?: string;
  severity?: 'error' | 'warning';
}

export function extractWarningsFromJobResult(
  jobResultJson: string | any
): PlanWarning[] {
  if (!jobResultJson) {
    return [];
  }

  try {
    // Parse if it's a string
    const json_result = typeof jobResultJson === 'string' 
      ? JSON.parse(jobResultJson) 
      : jobResultJson;

    if (!json_result || typeof json_result !== 'object') {
      return [];
    }

    const allWarnings: PlanWarning[] = [];
    
    // Handle two formats:
    // 1. Syndication: json_result.strategies
    // 2. Broadcast: json_result.data
    const strategiesObj = json_result.strategies || json_result.data;

    if (!strategiesObj) {
      return [];
    }

    // Iterate through all strategies and collect warnings
    for (const planName in strategiesObj) {
      const row = strategiesObj[planName];
      
      // Warnings are at top level in the strategy object
      const warnings = row?.warnings;
      
      if (warnings && Array.isArray(warnings)) {
        allWarnings.push(...warnings);
      }
    }

    return allWarnings;
  } catch (error) {
    console.error("Error extracting warnings from job result:", error);
    return [];
  }
}

